import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { requestsApi } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { ChevronDown, ChevronUp, CheckCircle2, XCircle, Star, Pencil } from 'lucide-react';

// Helper to format field values for display
const formatFieldValue = (field, value) => {
  if (value === null || value === undefined || value === '') return <span className="text-slate-400 italic">Not provided</span>;
  
  if (field?.type === 'boolean') {
    return value ? (
      <span className="flex items-center gap-1 text-emerald-600"><CheckCircle2 className="w-4 h-4" /> Yes</span>
    ) : (
      <span className="flex items-center gap-1 text-red-600"><XCircle className="w-4 h-4" /> No</span>
    );
  }
  
  if (field?.type === 'select' && field?.options) {
    const option = field.options.find(o => o.value === value);
    return option ? option.label : value;
  }
  
  return value;
};

export function ResponsesTab({ 
  request,
  feasibilityResponses,
  onEditResponse
}) {
  const { user } = useAuth();
  const [responseFields, setResponseFields] = useState([]);
  const [expandedResponses, setExpandedResponses] = useState({});

  // Fetch response fields for this request
  useEffect(() => {
    const fetchFields = async () => {
      try {
        const res = await requestsApi.getResponseFields(request.id);
        setResponseFields(res.data?.response_fields || []);
      } catch (error) {
        console.error('Failed to fetch response fields:', error);
      }
    };
    if (request.id && request.type === 'FEA') {
      fetchFields();
    }
  }, [request.id, request.type]);

  const toggleExpanded = (responseId) => {
    setExpandedResponses(prev => ({
      ...prev,
      [responseId]: !prev[responseId]
    }));
  };

  // Get field definition by ID
  const getFieldById = (fieldId) => {
    return responseFields.find(f => f.id === fieldId);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Feasibility Responses</span>
          {feasibilityResponses.length > 0 && (
            <Badge variant="outline" className="text-sm">
              {feasibilityResponses.length} response{feasibilityResponses.length !== 1 ? 's' : ''}
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {feasibilityResponses.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            {request.status === 'SUBMITTED' 
              ? 'Request has not been routed to consultants yet' 
              : 'No responses yet'
            }
          </div>
        ) : (
          <div className="space-y-4">
            {feasibilityResponses.map((response) => {
              const isExpanded = expandedResponses[response.id] !== false; // Default expanded
              const customResponses = response.custom_responses || {};
              const piIds = request.selected_pi_user_ids || [];
              const coiIds = request.selected_co_investigator_user_ids || [];
              const isSelectedPI = piIds.includes(response.consultant_user_id);
              const isSelectedCoI = coiIds.includes(response.consultant_user_id);
              const avatarBg = isSelectedPI
                ? 'bg-amber-500'
                : isSelectedCoI
                  ? 'bg-blue-600'
                  : (response.can_participate ? 'bg-emerald-600' : 'bg-slate-400');
              
              return (
                <div 
                  key={response.id} 
                  className="border border-slate-200 rounded-lg overflow-hidden" 
                  data-testid={`fea-response-${response.id}`}
                >
                  {/* Header - always visible */}
                  <div 
                    className="flex items-center justify-between p-4 bg-slate-50 cursor-pointer hover:bg-slate-100 transition-colors"
                    onClick={() => toggleExpanded(response.id)}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full ${avatarBg} flex items-center justify-center text-white font-medium`}>
                        {response.consultant_name?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-slate-900">{response.consultant_name}</span>
                          {isSelectedPI && (
                            <Badge className="bg-amber-100 text-amber-800 border border-amber-300" data-testid={`response-pi-badge-${response.id}`}>
                              <Star className="w-3 h-3 mr-1" /> Selected as PI
                            </Badge>
                          )}
                          {isSelectedCoI && (
                            <Badge className="bg-blue-100 text-blue-700 border border-blue-200" data-testid={`response-coi-badge-${response.id}`}>
                              Selected as Co-Investigator
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-slate-500">
                          {response.submitted_at ? new Date(response.submitted_at).toLocaleString() : 'Unknown date'}
                          {response.updated_at && ` · edited ${new Date(response.updated_at).toLocaleString()}`}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={
                        response.can_participate 
                          ? 'bg-emerald-100 text-emerald-700' 
                          : 'bg-red-100 text-red-700'
                      }>
                        {response.can_participate ? 'Can Participate' : 'Cannot Participate'}
                      </Badge>
                      {response.consultant_user_id === user?.id &&
                        !['COMPLETED', 'SIGNED', 'FAILED', 'WITHDRAWN'].includes(request?.status) &&
                        onEditResponse && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); onEditResponse(); }}
                            className="text-teal-700 border-teal-200 hover:bg-teal-50"
                            data-testid={`edit-my-response-${response.id}`}
                            title="Edit my response"
                          >
                            <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                          </Button>
                        )}
                      {isExpanded ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
                    </div>
                  </div>
                  
                  {/* Content - collapsible */}
                  {isExpanded && (
                    <div className="p-4 space-y-4 border-t border-slate-200">
                      {/* Render all response fields */}
                      {responseFields.map((field) => {
                        // Get value from custom_responses or legacy fields
                        let value;
                        if (field.id === 'can_participate') {
                          value = response.can_participate;
                        } else if (field.id === 'estimated_patient_pool') {
                          value = customResponses.estimated_patient_pool ?? response.estimated_patient_pool;
                        } else if (field.id === 'resources_available') {
                          value = customResponses.resources_available ?? response.resources_available;
                        } else if (field.id === 'notes') {
                          value = customResponses.notes ?? response.notes;
                        } else if (field.id === 'willing_to_participate_as_pi') {
                          value = customResponses.willing_to_participate_as_pi ?? response.willing_to_participate_as_pi;
                        } else {
                          value = customResponses[field.id];
                        }

                        // Skip can_participate as it's shown in header
                        if (field.id === 'can_participate') return null;

                        return (
                          <div key={field.id} className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-4">
                            <span className="text-sm font-medium text-slate-700 sm:w-48 shrink-0">
                              {field.label}:
                            </span>
                            <span className="text-sm text-slate-600 flex-1">
                              {formatFieldValue(field, value)}
                            </span>
                          </div>
                        );
                      })}
                      
                      {/* Show any additional custom responses not in responseFields */}
                      {Object.keys(customResponses).map(key => {
                        // Skip if already shown in responseFields
                        const existsInFields = responseFields.some(f => f.id === key);
                        if (existsInFields) return null;
                        
                        const value = customResponses[key];
                        if (value === null || value === undefined || value === '') return null;
                        
                        return (
                          <div key={key} className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-4">
                            <span className="text-sm font-medium text-slate-700 sm:w-48 shrink-0 capitalize">
                              {key.replace(/_/g, ' ')}:
                            </span>
                            <span className="text-sm text-slate-600 flex-1">
                              {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : value}
                            </span>
                          </div>
                        );
                      })}

                      {/* Legacy fields fallback if no responseFields */}
                      {responseFields.length === 0 && (
                        <>
                          {response.estimated_patient_pool && (
                            <div className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-4">
                              <span className="text-sm font-medium text-slate-700 sm:w-48 shrink-0">Estimated Patient Pool:</span>
                              <span className="text-sm text-slate-600 flex-1">{response.estimated_patient_pool}</span>
                            </div>
                          )}
                          {response.resources_available && (
                            <div className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-4">
                              <span className="text-sm font-medium text-slate-700 sm:w-48 shrink-0">Resources:</span>
                              <span className="text-sm text-slate-600 flex-1">{response.resources_available}</span>
                            </div>
                          )}
                          {response.notes && (
                            <div className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-4">
                              <span className="text-sm font-medium text-slate-700 sm:w-48 shrink-0">Notes:</span>
                              <span className="text-sm text-slate-600 flex-1">{response.notes}</span>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default ResponsesTab;
